import { Component } from '@theme/component';

/**
 * Overlay Trigger — Web Component for overlay toggle buttons.
 *
 * Wraps a trigger button that opens/closes the nearest overlay.
 * Discovers the overlay via the parent `[data-overlay-id]` container.
 * Syncs `aria-expanded` on the button when the overlay dialog state changes.
 *
 * Survives Section Rendering API morph — on reconnect, re-discovers
 * the overlay and rebinds listeners automatically via connectedCallback.
 *
 * @extends {Component}
 */
class OverlayTrigger extends Component {
	/** @type {HTMLElement|null} */
	#overlay = null;

	/** @type {HTMLDialogElement|null} */
	#dialog = null;

	/** @type {MutationObserver|null} */
	#observer = null;

	connectedCallback() {
		super.connectedCallback();

		const container = this.closest('[data-overlay-id]');
		if (!container) return;

		const overlayId = container.dataset.overlayId;
		this.#overlay = document.getElementById(overlayId);
		if (!this.#overlay) return;

		this.#dialog = this.#overlay.querySelector('dialog');

		// Set aria-controls on the trigger button
		const button = this.#getButton();
		if (button) {
			button.setAttribute('aria-controls', overlayId);
		}

		// Sync aria-expanded when dialog open state changes
		if (this.#dialog) {
			this.#observer = new MutationObserver(() => {
				const isOpen = this.#dialog.hasAttribute('open');
				const btn = this.#getButton();
				if (btn) btn.setAttribute('aria-expanded', String(isOpen));
			});
			this.#observer.observe(this.#dialog, { attributes: true, attributeFilter: ['open'] });
		}
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#observer?.disconnect();
		this.#observer = null;
		this.#overlay = null;
		this.#dialog = null;
	}

	/**
	 * Toggle the overlay — bound via on:click="/toggle" on trigger button.
	 */
	toggle() {
		this.#overlay?.toggle();
	}

	/**
	 * Find the trigger button inside this component.
	 * @returns {HTMLButtonElement|null}
	 */
	#getButton() {
		return this.querySelector('button, [role="button"]');
	}
}

if (!customElements.get('overlay-trigger')) {
	customElements.define('overlay-trigger', OverlayTrigger);
}
